import {
  enum_param,
  first_param,
  merge_search,
  parse_sort,
  sort_token,
  uuid_param
} from '../shared/url_query';

export const SOURCE = { SYSTEM: 'SYSTEM', MANUAL: 'MANUAL' };

export const WAIVER_STATUS = {
  NOT_WAIVED: 'NOT_WAIVED',
  WAIVED: 'WAIVED',
  FREE_PASS_APPLIED: 'FREE_PASS_APPLIED'
};

// A month-level penalty (overpayment recovery): no session, amount only.
export const MANUAL_TYPE = 'MANUAL';

export const ROW_ACTION = {
  CORRECT: 'correct',
  WAIVE: 'waive',
  UNWAIVE: 'unwaive',
  REVERT: 'revert',
  REMOVE: 'remove',
  APPLY_PASS: 'apply_pass',
  REMOVE_PASS: 'remove_pass'
};

export const BULK_ACTION = { WAIVE: 'WAIVE', APPLY_FREE_PASS: 'APPLY_FREE_PASS' };
export const GROUP_OUTCOME = { APPLIED: 'APPLIED', REFUSED: 'REFUSED' };
export const ITEM_OUTCOME = {
  APPLIED: 'APPLIED',
  UNCHANGED: 'UNCHANGED',
  REFUSED: 'REFUSED',
  GROUP_REFUSED: 'GROUP_REFUSED'
};

export const DIRECTIVE_ACTION = { REMOVE: 'REMOVE', APPLY: 'APPLY', RESET_AUTO: 'RESET_AUTO' };
export const DIRECTIVE_CHOICE = { MOVE: 'MOVE', KEEP_UNUSED: 'KEEP_UNUSED' };
// the month's own Free Pass Day, removed or given back by Ops (state.month_pass.actions).
export const MONTH_PASS_ACTION = { REMOVE: 'REMOVE', RESTORE: 'RESTORE' };
// each free-pass day's own offered actions, as the server computes them (pins/held/balance).
export const DAY_ACTION = { USE_PASS: 'use_pass', REMOVE_PASS: 'remove_pass' };
export const PLACED_SOURCE = { AUTO: 'AUTO', PIN: 'PIN' };
export const PASS_STATE = {
  PLACED: 'PLACED',
  HELD_PREV_MONTH_OPEN: 'HELD_PREV_MONTH_OPEN',
  HELD_LATER_MONTH_CLOSED: 'HELD_LATER_MONTH_CLOSED',
  NO_BALANCE: 'NO_BALANCE',
  NO_ELIGIBLE_DAY: 'NO_ELIGIBLE_DAY'
};

// overridden / orphaned list filters take "true" / "false"; "" sends no filter.
export const FILTER_BOOL = { TRUE: 'true', FALSE: 'false' };

// RESCHEDULE_ABUSE is no longer produced by the engine (replaced by the 4 CANCEL_RS_*/REPLACE_RS_*
// types below) but still exists on old rows, so it stays labelled and free-pass eligible.
export const FREE_PASS_ELIGIBLE_TYPES = [
  'CANCEL_24_1',
  'REPLACE_24_1',
  'REPLACE_SC',
  'RESCHEDULE_ABUSE',
  'CANCEL_RS_24_1',
  'REPLACE_RS_24_1'
];

// Labels match goserver penalties.TypeLabels (plain timing); rows prefer the server's
// penalty_type_label.
export const PENALTY_TYPES = [
  { penalty_type: 'TEACHER_NO_SHOW', label: 'Teacher no-show' },
  { penalty_type: 'LATE_JOIN', label: 'Late join' },
  { penalty_type: 'AV_LATE', label: 'Audio/video late' },
  { penalty_type: 'EARLY_EXIT', label: 'Early exit' },
  { penalty_type: 'CANCEL_1', label: 'Cancellation less than 1 hour before' },
  { penalty_type: 'CANCEL_24_1', label: 'Cancellation 1 to 24 hours before' },
  { penalty_type: 'REPLACE_1', label: 'Replacement less than 1 hour before' },
  { penalty_type: 'REPLACE_24_1', label: 'Replacement 1 to 24 hours before' },
  { penalty_type: 'REPLACE_SC', label: 'Replacement, student cancelled' },
  { penalty_type: 'RESCHEDULE_ABUSE', label: 'Reschedule abuse' },
  {
    penalty_type: 'CANCEL_RS_1',
    label: 'Rescheduled session cancelled less than 1 hour before'
  },
  {
    penalty_type: 'CANCEL_RS_24_1',
    label: 'Rescheduled session cancelled 1 to 24 hours before'
  },
  {
    penalty_type: 'REPLACE_RS_1',
    label: 'Rescheduled session replaced less than 1 hour before'
  },
  {
    penalty_type: 'REPLACE_RS_24_1',
    label: 'Rescheduled session replaced 1 to 24 hours before'
  },
  { penalty_type: MANUAL_TYPE, label: 'Manual (month-level)' }
];

export const type_label = (type) =>
  (PENALTY_TYPES.find((t) => t.penalty_type === type) || {}).label || type || '';

// Session-linked types only; MANUAL is the month-level add.
export const SESSION_TYPES = PENALTY_TYPES.filter((t) => t.penalty_type !== MANUAL_TYPE);

// split: a correction, a waiver and an add each take their own list; bulk WAIVE uses the waiver
// list.
export const REASON_CODES = {
  correct: ['SYSTEM_DETECTION_ERROR', 'WRONG_PENALTY_TYPE', 'TECHNICAL_ISSUE', 'OTHER'],
  waive: ['TEACHER_QUERY_APPROVED', 'TECHNICAL_ISSUE', 'GOODWILL_EXCEPTION', 'OTHER'],
  add: ['MISSED_BY_SYSTEM', 'AV_LATE_OBSERVED', 'OVERPAYMENT_RECOVERY', 'OTHER']
};

export const REASON_LABELS = {
  AV_LATE_OBSERVED: 'Audio/video late observed',
  GOODWILL_EXCEPTION: 'Goodwill exception',
  MISSED_BY_SYSTEM: 'Missed by the system',
  OTHER: 'Other',
  OVERPAYMENT_RECOVERY: 'Overpayment recovery',
  SYSTEM_DETECTION_ERROR: 'System detection error',
  TEACHER_QUERY_APPROVED: 'Teacher query approved',
  TECHNICAL_ISSUE: 'Technical issue',
  WRONG_PENALTY_TYPE: 'Wrong penalty type'
};

export const reason_options = (kind) =>
  (REASON_CODES[kind] || []).map((code) => ({ value: code, label: REASON_LABELS[code] || code }));

export const LIMITS = {
  AMOUNT_MAX_DECIMALS: 2,
  APPROVED_BY_MAX_LEN: 64,
  BULK_MAX_ITEMS: 200,
  LIST_LIMIT_DEFAULT: 100,
  LIST_LIMIT_MAX: 500,
  NOTE_MAX_LEN: 1024,
  NOTE_MIN_LEN: 3,
  PCT_AMOUNT_EPSILON: 0.01,
  PCT_MAX: 150,
  PCT_MAX_DECIMALS: 2
};

export const ERROR = {
  INVALID_REQUEST: 'INVALID_REQUEST',
  INVALID_NOTE: 'INVALID_NOTE',
  INVALID_PCT: 'INVALID_PCT',
  INVALID_AMOUNT: 'INVALID_AMOUNT',
  PCT_AMOUNT_MISMATCH: 'PCT_AMOUNT_MISMATCH',
  INVALID_PENALTY_TYPE: 'INVALID_PENALTY_TYPE',
  INVALID_REASON_CODE: 'INVALID_REASON_CODE',
  INVALID_APPROVED_BY: 'INVALID_APPROVED_BY',
  MIXED_EDIT: 'MIXED_EDIT',
  SESSION_REQUIRED: 'SESSION_REQUIRED',
  BULK_TOO_LARGE: 'BULK_TOO_LARGE',
  INVALID_DIRECTIVE: 'INVALID_DIRECTIVE',
  FORBIDDEN: 'FORBIDDEN',
  PENALTY_NOT_FOUND: 'PENALTY_NOT_FOUND',
  SESSION_NOT_FOUND: 'SESSION_NOT_FOUND',
  TEACHER_NOT_FOUND: 'TEACHER_NOT_FOUND',
  MONTH_CLOSED: 'MONTH_CLOSED',
  MONTH_NOT_OPEN: 'MONTH_NOT_OPEN',
  MONTH_OUTSIDE_WINDOW: 'MONTH_OUTSIDE_WINDOW',
  PREVIOUS_MONTH_NO_RECORD: 'PREVIOUS_MONTH_NO_RECORD',
  VERSION_CONFLICT: 'VERSION_CONFLICT',
  PENALTY_PASS_COVERED: 'PENALTY_PASS_COVERED',
  PENALTY_ORPHANED: 'PENALTY_ORPHANED',
  NOT_OVERRIDDEN: 'NOT_OVERRIDDEN',
  MANUAL_NOT_REVERTIBLE: 'MANUAL_NOT_REVERTIBLE',
  SYSTEM_NOT_REMOVABLE: 'SYSTEM_NOT_REMOVABLE',
  SESSION_NOT_OWNED: 'SESSION_NOT_OWNED',
  FREE_PASS_NOT_ON_DAY: 'FREE_PASS_NOT_ON_DAY',
  FREE_PASS_DAY_NOT_ELIGIBLE: 'FREE_PASS_DAY_NOT_ELIGIBLE',
  FREE_PASS_ALREADY_ON_DAY: 'FREE_PASS_ALREADY_ON_DAY',
  FREE_PASS_NO_BALANCE: 'FREE_PASS_NO_BALANCE',
  CONFIRM_DISPLACE_REQUIRED: 'CONFIRM_DISPLACE_REQUIRED',
  DIRECTIVE_CHANGED: 'DIRECTIVE_CHANGED',
  CONFIRM_LARGE_REQUIRED: 'CONFIRM_LARGE_REQUIRED',
  POSSIBLE_DUPLICATE: 'POSSIBLE_DUPLICATE',
  IDEMPOTENCY_MISMATCH: 'IDEMPOTENCY_MISMATCH',
  BAD_SORT: 'BAD_SORT',
  LOOKUP_FAILED: 'LOOKUP_FAILED'
};

// GET /penalties/list column sort (penalties_list_sort.go penaltyListSort); "" = the default id keyset.
export const LIST_SORT_KEYS = [
  'teacher_name',
  'session_time',
  'student_name',
  'type',
  'base_rate',
  'pct',
  'original',
  'charged',
  'status'
];
export const SORT_DIRS = ['asc', 'desc'];

export const WARNING = {
  RECOMPUTE_FAILED: 'RECOMPUTE_FAILED',
  TEACHER_HAS_NO_BASE_RATE: 'TEACHER_HAS_NO_BASE_RATE',
  MONTH_WILL_GO_NEGATIVE: 'MONTH_WILL_GO_NEGATIVE',
  PIN_DISPLACES_AUTO: 'PIN_DISPLACES_AUTO',
  PASS_COVERS_NEW_PENALTY: 'PASS_COVERS_NEW_PENALTY'
};

// ---- pure validation (the server re-validates; these keep a 400 from being the first feedback) ----

const text = (raw) => String(raw === null || raw === undefined ? '' : raw);

// Code points, as the server counts runes, never units.
export const char_length = (raw) => Array.from(text(raw).trim()).length;

export const note_error = (raw) => {
  const n = char_length(raw);
  if (n < LIMITS.NOTE_MIN_LEN) return `Enter a note of at least ${LIMITS.NOTE_MIN_LEN} characters.`;
  if (n > LIMITS.NOTE_MAX_LEN) return `The note must be at most ${LIMITS.NOTE_MAX_LEN} characters.`;
  return '';
};

export const approved_by_error = (raw) =>
  char_length(raw) > LIMITS.APPROVED_BY_MAX_LEN
    ? `"Approved by" must be at most ${LIMITS.APPROVED_BY_MAX_LEN} characters.`
    : '';

const DECIMAL = /^\d+(\.\d+)?$/;
const decimals = (s) => (s.indexOf('.') === -1 ? 0 : s.length - s.indexOf('.') - 1);

// A typed number, or null when blank or not a plain non-negative decimal.
export const parse_number = (raw) => {
  const s = text(raw).trim();
  return DECIMAL.test(s) ? Number(s) : null;
};

export const pct_error = (raw) => {
  const s = text(raw).trim();
  const n = parse_number(s);
  if (n === null || n > LIMITS.PCT_MAX || decimals(s) > LIMITS.PCT_MAX_DECIMALS) {
    return `Enter a percentage from 0 to ${LIMITS.PCT_MAX}, with at most ${LIMITS.PCT_MAX_DECIMALS} decimals.`;
  }
  return '';
};

export const amount_error = (raw) => {
  const s = text(raw).trim();
  const n = parse_number(s);
  if (n === null || decimals(s) > LIMITS.AMOUNT_MAX_DECIMALS) {
    return `Enter an amount in rupees with at most ${LIMITS.AMOUNT_MAX_DECIMALS} decimals.`;
  }
  return '';
};

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

// The basis turns a percentage into rupees and back; the server holds the same rule.
export const amount_from_pct = (basis, pct) =>
  basis > 0 && pct !== null ? round2((basis * pct) / 100) : null;

export const pct_from_amount = (basis, amount) =>
  basis > 0 && amount !== null ? round2((amount / basis) * 100) : null;

export const is_pass_eligible = (type) => FREE_PASS_ELIGIBLE_TYPES.indexOf(type) !== -1;

// default_pcts is the live map from GET /penalties/summary (policy_key/default_pct per type), never
// a build-time mirror. Missing/unloaded summary or a null policy value both leave the box empty.
export const default_pct_text = (type, default_pcts) => {
  const pct = (default_pcts || {})[type];
  return pct === null || pct === undefined ? '' : String(pct);
};

// A type change re-fills only an empty box or the previous type's default, never a figure Ops typed.
export const prefill_pct = (current, prev_type, next_type, default_pcts) =>
  text(current) === '' || text(current) === default_pct_text(prev_type, default_pcts)
    ? default_pct_text(next_type, default_pcts)
    : text(current);

// Correct penalty: the % follows the type. The row's own type keeps its current %; another type
// takes its rule % read-only, and only a type with no rule % (or none loaded) is typed by Ops.
export const correct_pct_for = (type, row, default_pcts) => {
  const r = row || {};
  if (type === r.penalty_type) {
    return { pct: text(r.penalty_pct), locked: true, hint: 'This penalty’s current percentage.' };
  }
  const rule = default_pct_text(type, default_pcts);
  if (rule !== '') return { pct: rule, locked: true, hint: 'The rule percentage for this type.' };
  return { pct: '', locked: false, hint: 'This type has no single rule percentage: enter it.' };
};

// A confirm answers only the body the server refused; any edit since makes the next send a fresh one.
export const unchanged_since = (sent, now) =>
  !!sent && JSON.stringify(sent) === JSON.stringify(now);

// ---- filter state and sort, kept in the URL ----
// teacher, type, status, source, changed=1|0, orphaned=1|0, sort=key|dir. Names never go in (PII):
// the teacher's name is read back from the listed rows.

const PENALTY_TYPE_VALUES = PENALTY_TYPES.map((t) => t.penalty_type);
const WAIVER_STATUS_VALUES = Object.values(WAIVER_STATUS);
const SOURCE_VALUES = Object.values(SOURCE);

export const FILTER_KEYS = [
  'teacher_uuid',
  'teacher_name',
  'penalty_type',
  'waiver_status',
  'source',
  'overridden',
  'orphaned'
];

// The tab's own URL keys, the legacy long names (old shared links: read, never written) included.
const URL_KEYS = [
  'teacher',
  'type',
  'status',
  'source',
  'changed',
  'orphaned',
  'sort',
  'teacher_uuid',
  'teacher_name',
  'penalty_type',
  'waiver_status',
  'overridden'
];

export const blank_filters = () => ({
  teacher_uuid: '',
  teacher_name: '',
  penalty_type: '',
  waiver_status: '',
  source: '',
  overridden: '',
  orphaned: ''
});

export const DEFAULT_LIST_SORT = { key: '', dir: '' };

// '1'|'0' in the URL, 'true'|'false' (FILTER_BOOL) on the wire; an old link's 'true'/'false' still reads.
const bool_from_url = (v) => {
  const s = (v || '').trim();
  if (s === '1' || s === FILTER_BOOL.TRUE) return FILTER_BOOL.TRUE;
  if (s === '0' || s === FILTER_BOOL.FALSE) return FILTER_BOOL.FALSE;
  return '';
};
const bool_to_url = (v) => ({ [FILTER_BOOL.TRUE]: '1', [FILTER_BOOL.FALSE]: '0' })[v] || '';

// The URL's filter state; an unrecognised or malformed value is dropped rather than sent (the backend
// would 400 on some of these).
export const parse_filters = (search) => {
  const qs = new URLSearchParams(typeof search === 'string' ? search : '');
  return {
    ...blank_filters(),
    teacher_uuid: uuid_param(first_param(qs, ['teacher', 'teacher_uuid'])),
    penalty_type: enum_param(first_param(qs, ['type', 'penalty_type']), PENALTY_TYPE_VALUES),
    waiver_status: enum_param(first_param(qs, ['status', 'waiver_status']), WAIVER_STATUS_VALUES),
    source: enum_param(qs.get('source'), SOURCE_VALUES),
    overridden: bool_from_url(first_param(qs, ['changed', 'overridden'])),
    orphaned: bool_from_url(qs.get('orphaned'))
  };
};

export const parse_list_sort = (search) =>
  parse_sort(
    new URLSearchParams(typeof search === 'string' ? search : '').get('sort'),
    LIST_SORT_KEYS,
    SORT_DIRS
  ) || DEFAULT_LIST_SORT;

// The URL the tab writes back: its own keys only, defaults left out, merged into whatever else is
// in the search (the shell's ?month=) rather than overwriting it wholesale.
export const filters_search = (search, filters, sort = DEFAULT_LIST_SORT) => {
  const f = filters || {};
  return merge_search(search, URL_KEYS, {
    teacher: uuid_param(f.teacher_uuid),
    type: enum_param(f.penalty_type, PENALTY_TYPE_VALUES),
    status: enum_param(f.waiver_status, WAIVER_STATUS_VALUES),
    source: enum_param(f.source, SOURCE_VALUES),
    changed: bool_to_url(f.overridden),
    orphaned: bool_to_url(f.orphaned),
    sort: sort_token(sort)
  });
};

// ---- filter bar (instant apply, no Apply button) ----

export const WAIVER_STATUS_OPTIONS = [
  { value: WAIVER_STATUS.NOT_WAIVED, label: 'Not waived' },
  { value: WAIVER_STATUS.WAIVED, label: 'Waived' },
  { value: WAIVER_STATUS.FREE_PASS_APPLIED, label: 'Free Pass applied' }
];
export const SOURCE_OPTIONS = [
  { value: SOURCE.SYSTEM, label: 'System' },
  { value: SOURCE.MANUAL, label: 'Added by Ops' }
];
export const BOOL_OPTIONS = [
  { value: FILTER_BOOL.TRUE, label: 'Yes' },
  { value: FILTER_BOOL.FALSE, label: 'No' }
];

// One filter changed; the list effect then reloads from the first page.
export const set_pe_filter = (filters, key, value) => ({ ...filters, [key]: value || '' });

// A cleared teacher drops its display name too.
export const set_pe_teacher = (filters, teacher) => {
  const t = teacher || {};
  const teacher_uuid = t.teacher_uuid || '';
  return { ...filters, teacher_uuid, teacher_name: teacher_uuid ? t.display || '' : '' };
};

export const clear_pe_filters = () => blank_filters();

export const pe_filters_on = (filters) =>
  FILTER_KEYS.some((k) => k !== 'teacher_name' && !!(filters || {})[k]);

const option_label = (options, v) => (options.find((o) => o.value === v) || {}).label || v;

// The sentence under the filters; count is the rows loaded so far, more = another page exists.
export const pe_filter_summary = ({ count, more, month_label, filters, loading }) => {
  if (loading) return null;
  const f = filters || blank_filters();
  const noun = count === 1 ? 'penalty' : 'penalties';
  const on = pe_filters_on(f);
  let lead = `${count} ${noun}`;
  if (more) lead = `first ${count} ${noun}`;
  else if (!on) lead = `all ${count} ${noun}`;
  const parts = [];
  if (f.teacher_uuid) parts.push(f.teacher_name ? `teacher ${f.teacher_name}` : 'one teacher');
  parts.push(f.penalty_type ? type_label(f.penalty_type) : 'all types');
  parts.push(f.waiver_status ? option_label(WAIVER_STATUS_OPTIONS, f.waiver_status) : 'any status');
  parts.push(f.source ? option_label(SOURCE_OPTIONS, f.source) : 'any source');
  if (f.overridden === FILTER_BOOL.TRUE) parts.push('changed by Ops');
  else if (f.overridden === FILTER_BOOL.FALSE) parts.push('not changed by Ops');
  else parts.push('changed by Ops or not');
  if (f.orphaned === FILTER_BOOL.TRUE) parts.push('orphaned only');
  else if (f.orphaned === FILTER_BOOL.FALSE) parts.push('not orphaned');
  else parts.push('orphaned or not');
  return on
    ? { lead, rest: ` for ${month_label}: ${parts.join(', ')}.` }
    : { lead, rest: ` for ${month_label}, ${parts.join(', ')}.` };
};
